import {
  Accordion,
  Button,
  Checkbox,
  useNotifications,
} from '@ph-react-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useModal } from '../../../hooks/useModal';
import { useArchiveUsers } from '../../../hooks/users/useArchiveUser';
import { useGetUsersRelatives } from '../../../hooks/users/useGetUsersRelatives';
import { ARCHIVE_USERS } from '../../../utils/constants/users/modals';
import { Role } from '../../../utils/enums/role.enum';
import { Dialog } from '../../shared/components/Dialog';

export const ArchiveMultiple = () => {
  const notification = useNotifications();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { close: closeModal } = useModal(ARCHIVE_USERS);
  const ids = searchParams.get('userId');
  const role = searchParams.get('role') ?? '';
  const modal = searchParams.get('modal');
  const isUnarchiving = modal === 'unarchive-user';
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { t } = useTranslation();
  const archiveUsers = useArchiveUsers();

  const showAccordion =
    parseInt(role) !== Role.Employee && parseInt(role) !== Role.Admin;

  const { data } = useGetUsersRelatives(
    ids?.split('&') as string[],
    isUnarchiving
  );

  const handleArchive = () => {
    let payload = {
      userIds: ids?.split('&') as string[],
      includeRelatives: true,
    };

    if (showAccordion) {
      payload = {
        userIds: [...(ids?.split('&') as string[]), ...selectedIds],
        includeRelatives: false,
      };
    }

    archiveUsers
      .mutateAsync(payload)
      .then(() => {
        closeModal();
        notification.success(t('USERS.ARCHIVE.ALERT.USERS_UPDATED'));
        queryClient.invalidateQueries({ queryKey: ['users'] });
      })
      .catch((resError) => {
        notification.danger(t(`NETWORK_ERRORS.${resError.errors[0]}`));
      });
  };

  const handleCheckboxChange = (id: string) => {
    setSelectedIds((prevState) => {
      if (prevState.includes(id)) {
        return prevState.filter((relativeId) => relativeId !== id);
      } else {
        return [...prevState, id];
      }
    });
  };

  const getConditionalProps = (userRole: number) => {
    const hideToggler = userRole === Role.Admin || userRole === Role.Employee;
    return { ...(hideToggler ? { togglerIconRef: '' } : {}) };
  };

  return (
    <Dialog
      dialogHeader={t('USERS.ACTIONS.ARCHIVE_MULTIPLE_USERS')}
      actionButton={
        <Button onClick={handleArchive}>{t('ACTIONS.CONFIRM')}</Button>
      }
      onClose={() => closeModal()}
    >
      {showAccordion && (
        <div className="dialog__archive__content">
          {data?.map((user) => (
            <div
              key={user.user.id}
              className="dialog__archive__accordion__wrapper"
            >
              <Accordion
                header={user.user.name}
                {...getConditionalProps(user.user.role)}
              >
                <div className="dialog__archive__accordion__body">
                  <>
                    {user?.parents?.map((parent) => (
                      <Checkbox
                        key={parent.id}
                        value={selectedIds.includes(parent.id)}
                        onChange={() => handleCheckboxChange(parent.id)}
                      >
                        {t('USERS.TABLE.ROLE.PARENT')}: {parent.name}
                      </Checkbox>
                    ))}
                    {user?.students?.map((student) => (
                      <Checkbox
                        key={student.id}
                        value={selectedIds.includes(student.id)}
                        onChange={() => handleCheckboxChange(student.id)}
                      >
                        {t('USERS.TABLE.ROLE.STUDENT')}: {student.name}
                      </Checkbox>
                    ))}
                  </>
                </div>
              </Accordion>
            </div>
          ))}
        </div>
      )}
      {!showAccordion && (
        <h2>{t('USERS.ARCHIVE.HEADING.ARE_YOU_SURE_ARCHIVE_MULTIPLE')}</h2>
      )}
    </Dialog>
  );
};
